import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { checkInLocationService } from '@/api/domains/checkInLocation';
import type { CheckInLocationItem } from '@/api/domains/checkInLocation';


const PAGE_SIZE = 20;

export interface UseBulkDisableModalOptions {
  isSelectAllMode: boolean;
  filters?: {
    hasGeolocation?: boolean | null;
    isRemoteWork?: boolean | null;
    searchText?: string | null;
    excludeLocationIds?: number[];
  };
}

export interface UseBulkDisableModalResult {
  allItems: CheckInLocationItem[];
  isLoadingMore: boolean;
  /**
   * Callback ref (no `useRef` object): el modal permanece montado mientras `isOpen` es `false`
   * y solo renderiza el `<div>` con scroll infinito cuando se abre — un `useRef` corriente no
   * dispararía el efecto que adjunta el listener de scroll si el nodo aparece después de que
   * el efecto ya corrió una vez. Un callback ref se ejecuta exactamente cuando React monta o
   * desmonta el nodo, sin depender de ningún prop adicional para saber cuándo reintentar.
   */
  scrollContainerRef: (node: HTMLDivElement | null) => void;
}

export function useBulkDisableModal({
  isSelectAllMode,
  filters,
}: UseBulkDisableModalOptions): UseBulkDisableModalResult {
  const pageRef = useRef(1);
  const hasMoreRef = useRef(true);
  const isLoadingMoreRef = useRef(false);
  // Se incrementa cada vez que el efecto de reinicio corre (activación o cambio de filtros).
  // Una petición de `loadMore` en vuelo captura la generación vigente al arrancar; si al
  // resolver ya no coincide con la actual, se descarta — evita que una respuesta con filtros
  // obsoletos se agregue sobre un `allItems` recién vaciado y desincronice pageRef/hasMoreRef.
  const generationRef = useRef(0);
  const [allItems, setAllItems] = useState<CheckInLocationItem[]>([]);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [scrollContainerNode, setScrollContainerNode] = useState<HTMLDivElement | null>(null);

  const loadMore = useCallback(async () => {
    if (!isSelectAllMode || isLoadingMoreRef.current || !hasMoreRef.current) return;
    const generation = generationRef.current;
    isLoadingMoreRef.current = true;
    setIsLoadingMore(true);
    try {
      const response = await checkInLocationService.list({
        page: pageRef.current,
        pageSize: PAGE_SIZE,
        isActive: true,
        hasGeolocation: filters?.hasGeolocation,
        isRemoteWork: filters?.isRemoteWork,
        searchText: filters?.searchText,
      });
      // Generación obsoleta: el reinicio ya vació allItems y reseteó pageRef/hasMoreRef para
      // los filtros actuales — aplicar esta respuesta la desincronizaría.
      if (generation !== generationRef.current) return;
      if (response.success && response.data) {
        setAllItems((prev) => [...prev, ...response.data!.items]);
        hasMoreRef.current = response.data.items.length === PAGE_SIZE;
        pageRef.current += 1;
      }
    } finally {
      if (generation === generationRef.current) {
        isLoadingMoreRef.current = false;
        setIsLoadingMore(false);
      }
    }
  }, [
    isSelectAllMode,
    filters?.hasGeolocation,
    filters?.isRemoteWork,
    filters?.searchText,
  ]);

  // Excluidos aplicados en la visualización (no al momento de traer los datos): así, si el
  // usuario deselecciona manualmente una fila ya cargada en páginas anteriores del scroll, esa
  // fila desaparece del listado sin necesidad de reiniciar la carga desde la página 1.
  const displayItems = useMemo(() => {
    const excludedIds = new Set(filters?.excludeLocationIds ?? []);
    if (excludedIds.size === 0) return allItems;
    return allItems.filter((item) => !excludedIds.has(item.id));
  }, [allItems, filters?.excludeLocationIds]);

  // El modal permanece montado (solo oculto) mientras `isSelectAllMode` se activa/desactiva y
  // mientras los filtros de la pantalla cambian, así que un efecto de solo-montaje nunca
  // volvería a dispararse. Se reinicia el scroll infinito desde la página 1 cada vez que se
  // activa el modo "seleccionar todo" o que cambian los filtros vigentes mientras está activo.
  useEffect(() => {
    if (!isSelectAllMode) return;
    generationRef.current += 1;
    pageRef.current = 1;
    hasMoreRef.current = true;
    isLoadingMoreRef.current = false;
    setAllItems([]);
    void loadMore();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isSelectAllMode,
    filters?.hasGeolocation,
    filters?.isRemoteWork,
    filters?.searchText,
  ]);

  useEffect(() => {
    const el = scrollContainerNode;
    if (!el || !isSelectAllMode) return;

    const handleScroll = () => {
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 80) {
        void loadMore();
      }
    };

    el.addEventListener('scroll', handleScroll);
    return () => el.removeEventListener('scroll', handleScroll);
  }, [scrollContainerNode, isSelectAllMode, loadMore]);

  return {
    allItems: displayItems,
    isLoadingMore,
    scrollContainerRef: setScrollContainerNode,
  };
}

export default useBulkDisableModal;
